/**
 * EditorToolbar: the Scene Builder shell (DOM).
 *
 * Lays out four regions, shown only while editing:
 *   - #editor-topbar  : transform mode (选择/移动/旋转/缩放) + placement mode
 *                       (吸附表面/自由深度/数值) + 保存 / 退出.
 *   - #editor-assets  : the config items (配置项目): a 场景配置 group (出生区/
 *                       天空盒 → property-panel config pages) + one button per
 *                       EditorAsset grouped by category — click SELECTS the
 *                       category: the right object list filters to its objects
 *                       and the 新增 button (object list) starts placement.
 *   - #editor-sidebar : mounts for the property panel + object list (filled by
 *                       SceneEditor.attachPanels).
 *
 * Mode buttons reflect `EditorState` and mutate it on click. Config item
 * buttons highlight the active `selectedAssetId`; an in-progress placement
 * highlights via `setPlacingActive` (the `placing` class).
 */

import type { EditorState, EditorMode, PlacementMode } from '../editor/EditorState';
import { SPAWN_AREA_CONFIG_ID, SKYBOX_CONFIG_ID } from '../editor/EditorState';
import type { EditorAssetLibrary, EditorAsset } from '../editor/EditorAssetLibrary';

export class EditorToolbar {
    private _el: HTMLElement;
    private _state: EditorState;
    private _assetLib: EditorAssetLibrary;
    private _onSelectAsset: (assetId: string) => void;
    private _onSave: () => void;
    private _onExit: () => void;

    private _assetButtons = new Map<string, HTMLButtonElement>();
    private _modeButtons = new Map<EditorMode, HTMLButtonElement>();
    private _placeModeButtons = new Map<PlacementMode, HTMLButtonElement>();
    private _unsubscribe: () => void;

    /** Mount points handed to SceneEditor.attachPanels. */
    propertyMount!: HTMLElement;
    listMount!: HTMLElement;

    constructor(
        state: EditorState,
        assetLib: EditorAssetLibrary,
        onSelectAsset: (assetId: string) => void,
        onSave: () => void,
        onExit: () => void
    ) {
        this._state = state;
        this._assetLib = assetLib;
        this._onSelectAsset = onSelectAsset;
        this._onSave = onSave;
        this._onExit = onExit;

        this._el = document.createElement('div');
        this._el.id = 'editor-shell';
        this._el.className = 'hidden';
        this._build();
        document.body.appendChild(this._el);

        this._unsubscribe = state.subscribe(() => this._sync());
        this._sync();
    }

    private _build(): void {
        // ── Top bar: grouped pills (变换 / 放置 / 操作) ──
        const top = document.createElement('div');
        top.id = 'editor-topbar';

        const transformGroup = this._group('变换');
        transformGroup.appendChild(this._modeBtn('选择', 'select'));
        transformGroup.appendChild(this._modeBtn('移动', 'move'));
        transformGroup.appendChild(this._modeBtn('旋转', 'rotate'));
        transformGroup.appendChild(this._modeBtn('缩放', 'scale'));
        top.appendChild(transformGroup);

        const placeGroup = this._group('放置');
        placeGroup.appendChild(this._placeModeBtn('吸附表面', 'raycastSurface'));
        placeGroup.appendChild(this._placeModeBtn('自由深度', 'freeDepth'));
        top.appendChild(placeGroup);

        const spacer = document.createElement('span');
        spacer.className = 'etb-spacer';
        top.appendChild(spacer);

        const actionGroup = this._group('操作');
        actionGroup.appendChild(this._actionBtn('保存', () => this._onSave()));
        actionGroup.appendChild(this._actionBtn('退出', () => this._onExit(), true));
        top.appendChild(actionGroup);
        this._el.appendChild(top);

        // ── Left: configuration items (placeable types) ──
        // Clicking an item selects it as the active 配置项目 — the right object
        // list then shows only that type's objects; 新增 (object list) starts
        // placement for it. The 场景配置 group (出生区/天空盒) selects a
        // scene-level config page in the property panel instead. The panel
        // title + 场景配置 label stay pinned; only the buttons scroll (the
        // scrollbar starts below the 场景配置 label).
        const assets = document.createElement('div');
        assets.id = 'editor-assets';
        const title = document.createElement('div');
        title.className = 'ea-title';
        title.textContent = '配置项目';
        assets.appendChild(title);

        const sceneLabel = document.createElement('div');
        sceneLabel.className = 'ea-group-label';
        sceneLabel.textContent = '场景配置';
        assets.appendChild(sceneLabel);

        const scroll = document.createElement('div');
        scroll.className = 'ea-scroll';
        this._sceneConfigBtn(scroll, SPAWN_AREA_CONFIG_ID, '出生区', '进场随机出生的矩形区域', '在右侧属性面板配置出生区');
        this._sceneConfigBtn(scroll, SKYBOX_CONFIG_ID, '天空盒', '场景天空盒贴图、强度与旋转', '在右侧属性面板配置天空盒');
        assets.appendChild(scroll);

        const categories = new Map<string, EditorAsset[]>();
        for (const a of this._assetLib.list()) {
            const list = categories.get(a.category) ?? [];
            list.push(a);
            categories.set(a.category, list);
        }
        for (const [, list] of categories) {
            const group = document.createElement('div');
            group.className = 'ea-group';
            for (const a of list) {
                const btn = document.createElement('button');
                btn.className = 'ea-prop';
                btn.innerHTML =
                    `<span class="ea-name">${a.name}</span>` +
                    (a.description ? `<span class="ea-desc">${a.description}</span>` : '');
                btn.title = `查看「${a.name}」对象列表`;
                btn.addEventListener('click', () => this._onSelectAsset(a.id));
                this._assetButtons.set(a.id, btn);
                group.appendChild(btn);
            }
            scroll.appendChild(group);
        }
        this._el.appendChild(assets);

        // ── Right: property + object list mounts ──
        const side = document.createElement('div');
        side.id = 'editor-sidebar';
        const listWrap = document.createElement('div');
        listWrap.className = 'es-section es-section--list';
        const listTitle = document.createElement('div');
        listTitle.className = 'es-title';
        listTitle.textContent = '对象列表';
        listWrap.appendChild(listTitle);
        this.listMount = document.createElement('div');
        this.listMount.className = 'es-mount';
        listWrap.appendChild(this.listMount);
        side.appendChild(listWrap);

        const propWrap = document.createElement('div');
        propWrap.className = 'es-section';
        const propTitle = document.createElement('div');
        propTitle.className = 'es-title';
        propTitle.textContent = '属性';
        propWrap.appendChild(propTitle);
        this.propertyMount = document.createElement('div');
        this.propertyMount.className = 'es-mount';
        propWrap.appendChild(this.propertyMount);
        side.appendChild(propWrap);

        this._el.appendChild(side);
    }

    /** A config item button for a scene-level config (出生区/天空盒): selecting
     *  it opens the matching page in the property panel (no placement). */
    private _sceneConfigBtn(parent: HTMLElement, id: string, name: string, desc: string, title: string): void {
        const btn = document.createElement('button');
        btn.className = 'ea-prop';
        btn.innerHTML =
            `<span class="ea-name">${name}</span>` +
            `<span class="ea-desc">${desc}</span>`;
        btn.title = title;
        btn.addEventListener('click', () => this._onSelectAsset(id));
        this._assetButtons.set(id, btn);
        parent.appendChild(btn);
    }

    /** A labeled pill group: [label][btn][btn]… Callers append buttons to it. */
    private _group(label: string): HTMLElement {
        const g = document.createElement('div');
        g.className = 'etb-group';
        const lbl = document.createElement('span');
        lbl.className = 'etb-group-label';
        lbl.textContent = label;
        g.appendChild(lbl);
        return g;
    }

    private _modeBtn(label: string, mode: EditorMode): HTMLButtonElement {
        const b = document.createElement('button');
        b.className = 'etb-btn';
        b.textContent = label;
        b.addEventListener('click', () => this._state.setMode(mode));
        this._modeButtons.set(mode, b);
        return b;
    }

    private _placeModeBtn(label: string, mode: PlacementMode): HTMLButtonElement {
        const b = document.createElement('button');
        b.className = 'etb-btn';
        b.textContent = label;
        b.addEventListener('click', () => this._state.setPlacementMode(mode));
        this._placeModeButtons.set(mode, b);
        return b;
    }

    private _actionBtn(label: string, on: () => void, exit = false): HTMLButtonElement {
        const b = document.createElement('button');
        b.className = 'etb-btn' + (exit ? ' etb-exit' : '');
        b.textContent = label;
        b.addEventListener('click', on);
        return b;
    }

    /** Sync mode/placement-mode button active states + the active config item
     *  highlight from EditorState. */
    private _sync(): void {
        for (const [m, b] of this._modeButtons) b.classList.toggle('active', this._state.mode === m);
        for (const [m, b] of this._placeModeButtons) b.classList.toggle('active', this._state.placementMode === m);
        for (const [id, b] of this._assetButtons) b.classList.toggle('active', this._state.selectedAssetId === id);
    }

    /** Highlight the asset currently being placed (or clear). Separate from the
     *  `active` category highlight so switching category mid-placement reads. */
    setPlacingActive(assetId: string | null): void {
        for (const [id, b] of this._assetButtons) b.classList.toggle('placing', id === assetId);
    }

    show(): void { this._el.classList.remove('hidden'); }
    hide(): void { this._el.classList.add('hidden'); }

    destroy(): void { this._unsubscribe(); this._el.remove(); }
}
